import moment from 'moment-timezone';

export interface WeekChunk {
  /** Stable key for cache (local calendar date of week start) */
  weekStartKey: string;
  rangeStartIso: string;
  rangeEndIso: string;
}

/**
 * Builds N consecutive 7-day chunks (Sun→Sat in the given timezone) from
 * the anchor instant. The wizard week is Sun-aligned end-to-end
 * (see ScheduleInterview/CLAUDE.md — "Don't reintroduce isoWeek");
 * legacy KInterviewScheduler + `ats_interviews/interviewers_schedule`
 * also require Sunday. Using `isoWeek` (Mon-aligned) would snap the
 * first chunk *backwards* and fetch a past week.
 */
export function buildWeekChunks(
  anchorIso: string,
  timezone: string,
  weekCount: number
): WeekChunk[] {
  const startM = moment(anchorIso).tz(timezone).startOf('week').startOf('day');
  const chunks: WeekChunk[] = [];
  for (let i = 0; i < weekCount; i += 1) {
    const ws = startM.clone().add(i, 'weeks');
    chunks.push({
      weekStartKey: ws.format('YYYY-MM-DD'),
      rangeStartIso: ws.toISOString(),
      rangeEndIso: ws.clone().add(7, 'days').toISOString(),
    });
  }
  return chunks;
}

export function chunkAtWeekOffset(
  anchorIso: string,
  timezone: string,
  weekOffset: number
): WeekChunk {
  const startM = moment(anchorIso)
    .tz(timezone)
    .startOf('week')
    .startOf('day')
    .add(weekOffset, 'weeks');
  return {
    weekStartKey: startM.format('YYYY-MM-DD'),
    rangeStartIso: startM.toISOString(),
    rangeEndIso: startM.clone().add(7, 'days').toISOString(),
  };
}

/**
 * `[from, to)` ISO instants for ATS `trigger_fetch` / polling, derived only from
 * the stable `YYYY-MM-DD` week bucket key + IANA zone. Used so React Query keys
 * stay stable (do not embed `rangeStartIso` in the query key).
 */
export function rangeIsoForWeekStartKey(
  weekStartKey: string,
  timezone: string
): { rangeStartIso: string; rangeEndIso: string } {
  const startM = moment.tz(weekStartKey, 'YYYY-MM-DD', timezone).startOf('day');
  return {
    rangeStartIso: startM.toISOString(),
    rangeEndIso: startM.clone().add(7, 'days').toISOString(),
  };
}
